"use client";

import { useState } from "react";
import type { ConsentState, CookieCategory } from "./cookie-consent.types";
import { TOGGLABLE_CATEGORIES } from "./cookie-consent.utils";

interface CategoryInfo {
  id: CookieCategory;
  label: string;
  description: string;
  locked?: boolean;
}

interface CookieConsentSettingsProps {
  labels: {
    necessary: string;
    necessaryDesc: string;
    analytics: string;
    analyticsDesc: string;
    functional: string;
    functionalDesc: string;
    marketing: string;
    marketingDesc: string;
    save: string;
    alwaysActive: string;
  };
  onSave: (categories: Pick<ConsentState, CookieCategory>) => void;
  initialState?: ConsentState | null;
}

export const CookieConsentSettings = ({
  labels,
  onSave,
  initialState,
}: CookieConsentSettingsProps) => {
  const [categories, setCategories] = useState<Record<CookieCategory, boolean>>({
    necessary: true,
    analytics: initialState?.analytics ?? false,
    functional: initialState?.functional ?? false,
    marketing: initialState?.marketing ?? false,
  });

  const allCategories: CategoryInfo[] = [
    { id: "necessary", label: labels.necessary, description: labels.necessaryDesc, locked: true },
    { id: "analytics", label: labels.analytics, description: labels.analyticsDesc },
    { id: "functional", label: labels.functional, description: labels.functionalDesc },
    { id: "marketing", label: labels.marketing, description: labels.marketingDesc },
  ];

  const handleToggle = (id: CookieCategory) => {
    if (id === "necessary") return;
    setCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = () => {
    onSave(categories);
  };

  return (
    <div className="flex flex-col gap-3 mt-4">
      {allCategories.map(({ id, label, description, locked }) => (
        <div
          key={id}
          className="flex items-center justify-between gap-4 rounded-lg px-4 py-3"
          style={{ background: "#1c1510", border: "1px solid #2d2118" }}
        >
          <div className="min-w-0">
            <div className="text-[0.8125rem] font-medium" style={{ color: "#F8F4EE" }}>
              {label}
            </div>
            <div className="text-[0.7rem] mt-0.5" style={{ color: "#8c7a6b" }}>
              {description}
            </div>
          </div>
          {locked ? (
            <span
              className="shrink-0 text-[0.65rem] font-medium px-2 py-1 rounded-full"
              style={{ background: "#2d2118", color: "#a87640" }}
            >
              {labels.alwaysActive}
            </span>
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={categories[id]}
              aria-label={`Alternar ${label}`}
              tabIndex={0}
              onClick={() => handleToggle(id)}
              className="shrink-0 relative w-10 h-5 rounded-full transition-colors duration-200"
              style={{ background: categories[id] ? "#a87640" : "#3d2f24" }}
            >
              <span
                className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200"
                style={{
                  background: "#F8F4EE",
                  transform: categories[id] ? "translateX(20px)" : "translateX(0)",
                }}
              />
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={handleSave}
        className="mt-1 h-10 rounded-full text-[0.8125rem] font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
        style={{ background: "#a87640", color: "#F8F4EE" }}
      >
        {labels.save}
      </button>
    </div>
  );
};
